import { FunctionOutlined, PlayCircleOutlined } from '@ant-design/icons'
import Editor, { useMonaco } from '@monaco-editor/react'
import { abbreviateText } from '@salescore/buff-common'
import type { ViewConfigTreeNode } from '@salescore/core'
import { Button, Col, Divider, Dropdown, Form, Menu, Row } from 'antd'
import { Array, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type CSSProperties, type ReactElement, useEffect, useRef } from 'react'
import { useRecoilValue } from 'recoil'
import { format } from 'sql-formatter'

import { NodePropertyPickerContent } from '../../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/common/NodePropertyPickerContent'
import { viewsContextAtom } from '../../../recoil/view/atoms'
import { useGetModel } from '../../../recoil/view/selectors/connectionsSelector'

const POSTGRES_SQL_STATEMENTS = [`SELECT`, `FROM`, `JOIN`, `WHERE`, `AND`, `LIMIT`, `OFFSET`]
const POSTGRES_SQL_FUNCTIONS = [{ label: t(`集約関数`), value: `array_agg()` }]

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function DynamicSelectOptionSqlEditor({ onExecute }: { onExecute?: () => Promise<void> }) {
  const getModel = useGetModel()
  const { models } = useRecoilValue(viewsContextAtom)
  const nodes: ViewConfigTreeNode[] = models.flatMap((model) => ({
    type: 'model',
    name: model.name,
    modelName: model.name,
  }))
  // suggest対象はSFAを跨いだ組織内全てのmodelとなる(dataExtension対象は弾くべきか?)
  const modelsWithNode = nodes
    .map((node) => {
      const model = getModel(node.modelName)
      if (model === undefined) {
        // eslint-disable-next-line array-callback-return
        return
      }
      return {
        node,
        model,
      }
    })
    .filter(isNotNullish)
  const propertiesWithNode = modelsWithNode.flatMap((x) =>
    x.model.properties.map((property) => ({
      node: x.node,
      model: x.model,
      property,
    })),
  )

  const monaco = useMonaco()
  // NOTE: Monacoに型がつかないため、anyを許容
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorReference = useRef<any>(null)

  useEffect(() => {
    if (monaco !== null) {
      const suggestions = [
        ...POSTGRES_SQL_STATEMENTS.map((command) => ({
          label: command,
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: `${command} `,
        })),
        ...POSTGRES_SQL_FUNCTIONS.map((command) => ({
          label: command.value,
          detail: command.label,
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: `${command.value} `,
        })),
        ...pipe(
          modelsWithNode,
          Array.dedupeWith((a, b) => a.node.name === b.node.name),
          Array.map((modelWithNode) => ({
            label: modelWithNode.model.label,
            detail: modelWithNode.model.label,
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: `"${modelWithNode.node.name}" `,
          })),
        ),
        ...pipe(
          propertiesWithNode,
          Array.dedupeWith((a, b) => `${a.node.name}.${a.property.name}` === `${b.node.name}.${b.property.name}`),
          Array.map((propertyWithNode) => ({
            label: `${propertyWithNode.node.name}.${propertyWithNode.property.name}`,
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            detail: `${propertyWithNode.property.label ?? ''}(${propertyWithNode.model.label ?? ''})`,
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: `"${propertyWithNode.node.name}"."${propertyWithNode.property.name}" `,
          })),
        ),
      ]
      monaco.languages.registerCompletionItemProvider('sql', {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        provideCompletionItems(model: any, position: any) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
          const word = model.getWordUntilPosition(position)
          return {
            suggestions: pipe(
              suggestions,
              Array.dedupeWith((a, b) => a.insertText === b.insertText),
              Array.map((x) => ({
                ...x,
                range: {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
                  startLineNumber: position.lineNumber,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
                  endLineNumber: position.lineNumber,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
                  startColumn: word.startColumn,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
                  endColumn: word.endColumn,
                },
              })),
            ),
          }
        },
      })
    }
  }, [monaco])

  const insert = (text: string) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const editor = editorReference.current
    if (editor === null) {
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
    const selection = editor.getSelection()
    // https://qiita.com/lumis/items/311b8c39d61312957195#%E3%82%A8%E3%83%87%E3%82%A3%E3%82%BF%E3%81%A7%E7%B7%A8%E9%9B%86%E3%81%99%E3%82%8B
    // editor.executeEdits(``, [{ range: selection ?? new Range(1, 1, 1, 1), text }])
    if (isNotNullish(selection)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
      editor.executeEdits(``, [{ range: selection, text }])
    }
  }

  return (
    <div
      className="overflow-hidden"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
      onKeyDown={async (e) => {
        if (onExecute !== undefined && e.code === 'Enter' && (e.metaKey || e.shiftKey)) {
          await onExecute()
          // eslint-disable-next-line unicorn/no-late-event-control
          e.preventDefault()
          // eslint-disable-next-line unicorn/no-late-event-control
          e.stopPropagation()
        }
      }}
    >
      <Row>
        <Col span={14}>
          <MonacoEditorNavigationHeader title={t(`SQLエディタ`)}>
            <>
              <SqlFunctionDropdown
                onFinish={(text) => {
                  insert(text)
                }}
              >
                <MonacoEditorNavigationButton icon={<FunctionOutlined />}>{t(`関数`)}</MonacoEditorNavigationButton>
              </SqlFunctionDropdown>
              <MonacoEditorNavigationButton
                icon={<FunctionOutlined />}
                onClick={() => {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  const editor = editorReference.current
                  if (editor === null) {
                    return
                  }
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-type-assertion
                  const sql = editor.getModel()?.getValue() as string | null
                  if (sql === null) {
                    return
                  }
                  const formatted = format(sql, { language: 'postgresql' })
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
                  editor.getModel()?.setValue(formatted)
                }}
              >
                {t(`フォーマット`)}
              </MonacoEditorNavigationButton>
              {onExecute !== undefined && (
                <MonacoEditorNavigationButton
                  icon={<PlayCircleOutlined />}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
                  onClick={async () => {
                    await onExecute()
                  }}
                >
                  {`${t(`実行`)}(Shift+Enter)`}
                </MonacoEditorNavigationButton>
              )}
            </>
          </MonacoEditorNavigationHeader>
          <Form.Item name="sql">
            <Editor
              height={540}
              theme="vs-dark"
              defaultLanguage="sql"
              onMount={(editor, monaco) => {
                editorReference.current = editor
              }}
            />
          </Form.Item>
        </Col>
        <Col span={10}>
          <div className="pl-8">
            <NodePropertyPickerContent
              showInternalName
              propertiesWithNode={propertiesWithNode}
              onFinish={(nodePropertyName) => {
                insert(`"${nodePropertyName.nodeName}"."${nodePropertyName.propertyName}"`)
              }}
            />
          </div>
        </Col>
      </Row>
    </div>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function SqlFunctionDropdown({ onFinish, children }: { onFinish: (text: string) => void; children: ReactElement }) {
  const menu = (
    <Menu
      items={POSTGRES_SQL_FUNCTIONS.map(({ value, label }) => ({
        key: value,
        label: (
          <div>
            {label}
            <span className="ml-2 opacity-60">{abbreviateText(value, 15)}</span>
          </div>
        ),
        onClick: () => {
          onFinish(value)
        },
      }))}
    />
  )

  return (
    // eslint-disable-next-line sonarjs/deprecation
    <Dropdown overlay={menu} trigger={['click']}>
      {children}
    </Dropdown>
  )
}

const navigationStyle: CSSProperties = {
  backgroundColor: `#1E1E1E`,
  color: `white`,
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function MonacoEditorNavigationButton({
  children,
  icon,
  onClick,
}: {
  children: string
  icon: ReactElement
  onClick?: () => void
}) {
  return (
    <>
      <Button
        style={{
          ...navigationStyle,
        }}
        className="opacity-80 hover:bg-gray-500  hover:opacity-100"
        type="text"
        icon={icon}
        onClick={onClick}
      >
        {children}
      </Button>
      <Divider
        type="vertical"
        style={{
          borderColor: '#444',
          height: 30,
          top: 0,
        }}
      />
    </>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function MonacoEditorNavigationHeader({ title, children }: { title: string; children: ReactElement }) {
  return (
    <div
      className="flex items-center px-4 align-middle"
      style={{
        ...navigationStyle,
        // backgroundColor: `#050505`,
        borderBottom: `1px solid #444`,
      }}
    >
      <div className="opacity-80">{title}</div>
      <Divider
        type="vertical"
        style={{
          borderColor: '#444',
          height: 30,
          top: 0,
        }}
      />
      {children}
    </div>
  )
}
